import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboardService';
import { Sprout, MapPin, Users, TrendingUp, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showFullMap, setShowFullMap] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    trials: { total: 0, mlt: 0, art: 0, other: 0 },
    locations: { total: 0, mltLocations: 0, artLocations: 0 },
    incharges: { total: 0, admin: 0, scientists: 0, gotnOfficials: 0, locationIncharge: 0 }
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  

  // Load dashboard data from API
  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const [
        // summaryResponse, 
        activitiesResponse,
        trialStatsResponse,
        userCountResponse,
        activeLocationsResponse
      ] = await Promise.all([
        // dashboardService.getDashboardSummary(),
        dashboardService.getRecentTrials(),
        dashboardService.getTrialStats(),
        dashboardService.getTotalUserCount(),
        dashboardService.getActiveLocationsCount()
      ]);
      
      // if (summaryResponse.success) {
      //   setDashboardStats(summaryResponse.data || summaryResponse);
      // }
      
      if (activitiesResponse.success) {
        const trials = Array.isArray(activitiesResponse.data)
          ? activitiesResponse.data
          : [];
        setRecentActivities(trials);
      }

      // Update trial stats from the new API endpoint (be permissive about response shape)
      try {
        let trialSummary = null;
        if (trialStatsResponse) {
          if (trialStatsResponse.data && trialStatsResponse.data.summary) trialSummary = trialStatsResponse.data.summary;
          else if (trialStatsResponse.summary) trialSummary = trialStatsResponse.summary;
          else if (trialStatsResponse.data && typeof trialStatsResponse.data === 'object') trialSummary = trialStatsResponse.data;
          else trialSummary = trialStatsResponse;
        }

        if (trialSummary) {
          const total = Number(trialSummary.total_trials ?? trialSummary.total ?? 0) || 0;
          const art = Number(trialSummary.art_trials ?? trialSummary.art ?? 0) || 0;
          const mlt = Number(trialSummary.mlt_trials ?? trialSummary.mlt ?? 0) || 0;
          const other = Number(trialSummary.other_trials ?? trialSummary.other ?? 0) || 0;

          setDashboardStats(prev => ({
            ...prev,
            trials: {
              total,
              art,
              mlt,
              other
            }
          }));

          // Debug log to help verify data mapping
          // eslint-disable-next-line no-console
          console.log('Loaded trial stats:', { total, art, mlt, other });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to parse trial stats response', e);
      }

      // Update incharges count from user count API
      try {
        let userCount = 0;
        if (userCountResponse) {
          if (userCountResponse.data && userCountResponse.data.totalUsers) {
            userCount = Number(userCountResponse.data.totalUsers) || 0;
          } else if (userCountResponse.totalUsers) {
            userCount = Number(userCountResponse.totalUsers) || 0;
          } else if (userCountResponse.data && typeof userCountResponse.data === 'number') {
            userCount = Number(userCountResponse.data) || 0;
          }
        }

        if (userCount > 0) {
          setDashboardStats(prev => ({
            ...prev,
            incharges: {
              ...prev.incharges,
              total: userCount
            }
          }));

          // Debug log to help verify data mapping
          // eslint-disable-next-line no-console
          console.log('Loaded user count:', userCount);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to parse user count response', e);
      }

      // Update active locations count from API
      try {
        let activeLocationsCount = 0;
        if (activeLocationsResponse) {
          if (activeLocationsResponse.data && activeLocationsResponse.data.activeLocations) {
            activeLocationsCount = Number(activeLocationsResponse.data.activeLocations) || 0;
          } else if (activeLocationsResponse.activeLocations) {
            activeLocationsCount = Number(activeLocationsResponse.activeLocations) || 0;
          } else if (activeLocationsResponse.data && typeof activeLocationsResponse.data === 'number') {
            activeLocationsCount = Number(activeLocationsResponse.data) || 0;
          }
        }

        if (activeLocationsCount >= 0) {
          setDashboardStats(prev => ({
            ...prev,
            locations: {
              ...prev.locations,
              total: activeLocationsCount
            }
          }));

          // Debug log to help verify data mapping
          // eslint-disable-next-line no-console
          console.log('Loaded active locations count:', activeLocationsCount);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to parse active locations response', e);
      }
    } catch (error) {
      console.warn('Failed to load dashboard data from API, using mock data:', error.message);
      // Keep existing mock data if API fails
    } finally {
      setIsLoading(false);
    }
  };




  // Load data on component mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Fallback mock data for recent activities if API fails
  const fallbackActivities = [
    {
      id: 1,
      type: 'trial',
      action: 'New trial created',
      description: 'Paddy variety trial started in Coimbatore',
      time: '2 hours ago',
      status: 'active',
      iconBgClass: 'bg-green-100'
    },
    {
      id: 2,
      type: 'location',
      action: 'New location added',
      description: 'Salem East block added to database',
      time: '4 hours ago',
      status: 'completed',
      iconBgClass: 'bg-blue-100'
    },
    {
      id: 3,
      type: 'incharge',
      action: 'Officer assigned',
      description: 'Dr. Priya Sharma assigned to Erode region',
      time: '6 hours ago',
      status: 'active',
      iconBgClass: 'bg-purple-100'
    },
    {
      id: 4,
      type: 'observation',
      action: 'Field observation completed',
      description: 'Crop growth monitoring in Block A',
      time: '1 day ago',
      status: 'completed',
      iconBgClass: 'bg-orange-100'
    },
    {
      id: 5,
      type: 'soil',
      action: 'Soil analysis completed',
      description: 'pH and NPK analysis for Coimbatore North',
      time: '2 days ago',
      status: 'completed',
      iconBgClass: 'bg-teal-100'
    }
  ];

  // Helper function to format timestamp
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffHours < 1) return 'Just now';
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    } catch (e) {
      return 'Recently';
    }
  };

  const getTrialCreatorName = (trial) =>
    trial.created_by_name ||
    trial.user_name ||
    trial.creator_name ||
    trial.created_by?.name ||
    trial.created_by?.username ||
    '';

  // Map /dashboard/recent-trials items to Recent Activities list rows
  const transformRecentTrials = (trials) => {
    return trials.map((trial, index) => {
      const code = trial.code || '';
      const title = trial.title || 'Trial';
      const typeLabel = trial.trial_type ? `${trial.trial_type} trial` : '';
      const cropSeason = [trial.crop_name, trial.season_name, trial.year]
        .filter(Boolean)
        .join(' · ');
      const extra = [typeLabel, cropSeason].filter(Boolean).join(' — ');
      const description = extra
        ? `${code} - ${title} · ${extra}`
        : `${code} - ${title}`;

      const rawStatus = (trial.status || 'active').toString().toLowerCase();

      return {
        id: trial.id ?? index + 1,
        type: 'trial',
        action: 'Trial created',
        description,
        time: formatTimestamp(trial.created_at),
        status: rawStatus,
        iconBgClass: 'bg-green-100',
        user_name: getTrialCreatorName(trial)
      };
    });
  };

  const displayActivities =
    recentActivities.length > 0
      ? transformRecentTrials(recentActivities)
      : fallbackActivities;

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'trial':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'location':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          </svg>
        );
      case 'incharge':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        );
      case 'observation':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        );
      case 'soil':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a1 1 0 01-1.732 0l-.318-.158a6 6 0 00-3.86-.517l-2.387.477a2 2 0 00-1.022.547M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Welcome back, {user?.name || 'User'}!
        </h1>
        <p className="text-primary-100">
          Here's what's happening with your agricultural trials today.
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Total Trials Card */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 overflow-hidden">
          <div className="flex">
            <div className="w-16 md:w-20 bg-blue-600 flex items-center justify-center">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Sprout className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <div className="flex-1 p-3 md:p-4">
              <p className="text-xl md:text-2xl font-bold text-blue-600">{dashboardStats.trials.total}</p>
              <p className="text-xs md:text-sm font-medium text-gray-600">Total Trials</p>
              <div className="mt-2 flex flex-col sm:flex-row sm:space-x-2 space-y-1 sm:space-y-0 text-xs">
                <span className="text-blue-600">MLT: {dashboardStats.trials.mlt}</span>
                <span className="text-green-600">ART: {dashboardStats.trials.art}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Active Locations Card */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 overflow-hidden">
          <div className="flex">
            <div className="w-16 md:w-20 bg-green-600 flex items-center justify-center">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <div className="flex-1 p-3 md:p-4">
              <p className="text-xl md:text-2xl font-bold text-green-600">{dashboardStats.locations.total}</p>
              <p className="text-xs md:text-sm font-medium text-gray-600">Active Locations</p>
              <div className="mt-2 flex flex-col sm:flex-row sm:space-x-2 space-y-1 sm:space-y-0 text-xs">
                {/* <span className="text-blue-600">MLT: {dashboardStats.locations.mltLocations}</span>
                <span className="text-green-600">ART: {dashboardStats.locations.artLocations}</span> */}
              </div>
            </div>
          </div>
        </div>

        {/* Total Incharges Card */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 overflow-hidden">
          <div className="flex">
            <div className="w-16 md:w-20 bg-purple-600 flex items-center justify-center">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <div className="flex-1 p-3 md:p-4">
              <p className="text-xl md:text-2xl font-bold text-purple-600">{dashboardStats.incharges.total}</p>
              <p className="text-xs md:text-sm font-medium text-gray-600">Total Incharges</p>
              <div className="mt-2 flex flex-col sm:flex-row sm:space-x-2 space-y-1 sm:space-y-0 text-xs">
                {/* <span className="text-blue-600">Scientists: {dashboardStats.incharges.scientists}</span>
                <span className="text-green-600">Officials: {dashboardStats.incharges.gotnOfficials}</span> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
        {/* Trial Status Distribution Pie Chart */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
            <h2 className="text-base md:text-lg font-semibold text-gray-900">Trial Status Distribution</h2>
            <div className="text-left sm:text-right">
              <p className="text-xs md:text-sm text-gray-600">Total Trials</p>
              <p className="text-lg md:text-xl font-bold text-gray-900">{dashboardStats.trials.total}</p>
            </div>
          </div>
          <div className="p-4 md:p-6">
            <div className="flex items-center justify-center h-48 md:h-64">
              {/* Pie Chart Visualization */}
              <div className="relative w-32 h-32 md:w-48 md:h-48">
                {/* Active trials slice (65%) - full circle */}
                <div className="absolute inset-0 bg-green-600 rounded-full"></div>
                {/* Planning slice (25%) - right quarter */}
                <div className="absolute inset-0 bg-blue-600 rounded-full" style={{
                  clipPath: 'polygon(50% 50%, 50% 0%, 100% 0%, 100% 50%)'
                }}></div>
                {/* Completed slice (10%) - small right slice */}
                <div className="absolute inset-0 bg-orange-500 rounded-full" style={{
                  clipPath: 'polygon(50% 50%, 100% 50%, 100% 0%, 90% 0%, 90% 50%)'
                }}></div>
                {/* Center circle */}
                <div className="absolute inset-3 md:inset-4 bg-white rounded-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-lg md:text-2xl font-bold text-gray-900">65%</div>
                    <div className="text-xs md:text-sm text-gray-600">Active</div>
                  </div>
                </div>
          </div>
        </div>

            {/* Chart Legend */}
            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 bg-green-600 rounded-full"></div>
                  <span className="text-sm text-gray-700">Active Trials</span>
                </div>
                <span className="text-sm font-medium text-gray-900">65%</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
                  <span className="text-sm text-gray-700">Planning Phase</span>
                </div>
                <span className="text-sm font-medium text-gray-900">25%</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Completed</span>
                </div>
                <span className="text-sm font-medium text-gray-900">10%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Crop-wise Trials Bar Chart */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
            <h2 className="text-base md:text-lg font-semibold text-gray-900">Crop-wise Trial Distribution</h2>
            <div className="text-left sm:text-right">
              <p className="text-xs md:text-sm text-gray-600">Total Trials</p>
              <p className="text-lg md:text-xl font-bold text-gray-900">{dashboardStats.trials.total}</p>
            </div>
          </div>
          <div className="p-4 md:p-6">
            {/* Bar Chart - Always Visible */}
            <div className="h-64 flex items-end justify-between space-x-2 md:space-x-3 overflow-x-auto">
              {/* Bar Chart Visualization */}
              <div className="flex-1 flex flex-col items-center">
                <div className="text-xs text-gray-600 mb-2 font-medium">18</div>
                <div className="w-full bg-blue-500 rounded-t shadow-sm" style={{height: '180px'}}></div>
                <div className="text-xs text-gray-600 mt-2 font-medium">Rice</div>
              </div>
              <div className="flex-1 flex flex-col items-center">
                <div className="text-xs text-gray-600 mb-2 font-medium">15</div>
                <div className="w-full bg-green-500 rounded-t shadow-sm" style={{height: '150px'}}></div>
                <div className="text-xs text-gray-600 mt-2 font-medium">Maize</div>
              </div>
              <div className="flex-1 flex flex-col items-center">
                <div className="text-xs text-gray-600 mb-2 font-medium">12</div>
                <div className="w-full bg-yellow-500 rounded-t shadow-sm" style={{height: '120px'}}></div>
                <div className="text-xs text-gray-600 mt-2 font-medium">Wheat</div>
              </div>
              <div className="flex-1 flex flex-col items-center">
                <div className="text-xs text-gray-600 mb-2 font-medium">8</div>
                <div className="w-full bg-orange-500 rounded-t shadow-sm" style={{height: '120px'}}></div>
                <div className="text-xs text-gray-600 mt-2 font-medium">Pulses</div>
              </div>
              <div className="flex-1 flex flex-col items-center">
                <div className="text-xs text-gray-600 mb-2 font-medium">6</div>
                <div className="w-full bg-purple-500 rounded-t shadow-sm" style={{height: '90px'}}></div>
                <div className="text-xs text-gray-600 mt-2 font-medium">Oilseeds</div>
              </div>
              <div className="flex-1 flex flex-col items-center">
                <div className="text-xs text-gray-600 mb-2 font-medium">4</div>
                <div className="w-full bg-pink-500 rounded-t shadow-sm" style={{height: '60px'}}></div>
                <div className="text-xs text-gray-600 mt-2 font-medium">Vegetables</div>
              </div>
              <div className="flex-1 flex flex-col items-center">
                <div className="text-xs text-gray-600 mb-2 font-medium">3</div>
                <div className="w-full bg-teal-500 rounded-t shadow-sm" style={{height: '45px'}}></div>
                <div className="text-xs text-gray-600 mt-2 font-medium">Fruits</div>
              </div>
              <div className="flex-1 flex flex-col items-center">
                <div className="text-xs text-gray-600 mb-2 font-medium">2</div>
                <div className="w-full bg-indigo-500 rounded-t shadow-sm" style={{height: '30px'}}></div>
                <div className="text-xs text-gray-600 mt-2 font-medium">Spices</div>
              </div>
            </div>
            
            {/* Y-axis labels */}
            <div className="mt-4 flex justify-between text-xs text-gray-500">
              <span>0</span>
              <span>5</span>
              <span>10</span>
              <span>15</span>
              <span>20</span>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-4 md:px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-base md:text-lg font-semibold text-gray-900">Location Map</h2>
          <button
            onClick={() => setShowFullMap(!showFullMap)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            title={showFullMap ? "Show small map" : "Show full map"}
          >
            {showFullMap ? (
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
          </button>
        </div>
        <div className="p-4 md:p-6">
          {showFullMap ? (
            <div className="h-full w-full">
              {/* Placeholder for full map component */}
              <p className="text-center text-gray-500">Full map view will be implemented here.</p>
            </div>
          ) : (
            <div className="h-64 md:h-96 w-full">
              {/* Small Tamil Nadu Map */}
              <div className="bg-white rounded-lg border-2 border-gray-200 h-full flex items-center justify-center relative overflow-hidden">
                <svg 
                  viewBox="0 0 400 300" 
                  className="w-full h-full p-4"
                  style={{ maxWidth: '100%', height: 'auto' }}
                >
                  {/* Tamil Nadu State Outline */}
                  <path
                    d="M 50 50 L 150 30 L 250 40 L 320 60 L 350 100 L 340 150 L 320 200 L 280 250 L 200 270 L 120 280 L 80 250 L 60 200 L 50 150 Z"
                    fill="#f0f9ff"
                    stroke="#3b82f6"
                    strokeWidth="2"
                    className="hover:fill-blue-100 transition-colors cursor-pointer"
                  />
                  
                  {/* District Markers */}
                  <circle cx="120" cy="120" r="6" fill="#3b82f6" className="cursor-pointer hover:r-8 transition-all">
                    <title>Coimbatore - 3 Trials</title>
                  </circle>
                  <text x="120" y="140" textAnchor="middle" className="text-xs font-medium fill-gray-700">Coimbatore</text>
                  
                  <circle cx="180" cy="100" r="5" fill="#10b981" className="cursor-pointer hover:r-7 transition-all">
                    <title>Salem - 2 Trials</title>
                  </circle>
                  <text x="180" y="120" textAnchor="middle" className="text-xs font-medium fill-gray-700">Salem</text>
                  
                  <circle cx="200" cy="140" r="5" fill="#8b5cf6" className="cursor-pointer hover:r-7 transition-all">
                    <title>Erode - 2 Trials</title>
                  </circle>
                  <text x="200" y="160" textAnchor="middle" className="text-xs font-medium fill-gray-700">Erode</text>
                  
                  <circle cx="280" cy="180" r="4" fill="#f59e0b" className="cursor-pointer hover:r-6 transition-all">
                    <title>Madurai - 1 Trial</title>
                  </circle>
                  <text x="280" y="200" textAnchor="middle" className="text-xs font-medium fill-gray-700">Madurai</text>
                  
                  <circle cx="320" cy="80" r="5" fill="#ef4444" className="cursor-pointer hover:r-7 transition-all">
                    <title>Chennai - 2 Trials</title>
                  </circle>
                  <text x="320" y="100" textAnchor="middle" className="text-xs font-medium fill-gray-700">Chennai</text>
                  
                  <circle cx="240" cy="160" r="4" fill="#06b6d4" className="cursor-pointer hover:r-6 transition-all">
                    <title>Trichy - 1 Trial</title>
                  </circle>
                  <text x="240" y="180" textAnchor="middle" className="text-xs font-medium fill-gray-700">Trichy</text>
                </svg>
                
                {/* Map Overlay Info */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                    <p className="text-xs text-gray-600 text-center">
                      Hover over districts to see trial counts • Click for full view
                    </p>
                  </div>
                </div>
                
                {/* Full Map Button */}
                <div className="absolute top-4 right-4">
                  <button 
                    onClick={() => setShowFullMap(true)}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-sm shadow-lg"
                  >
                    View Full Map
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Recent Activities */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-4 md:px-6 py-4 border-b border-gray-200">
          <h2 className="text-base md:text-lg font-semibold text-gray-900">Recent Activities</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {displayActivities.map((activity) => (
            <div key={activity.id} className="px-4 md:px-6 py-4 flex items-start space-x-3">
              <div className={`p-2 rounded-lg ${activity.iconBgClass || 'bg-green-100'}`}>
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                <p className="text-sm text-gray-600">{activity.description}</p>
                <div className="flex flex-col sm:flex-row sm:items-center mt-1 space-y-1 sm:space-y-0 sm:space-x-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                    {activity.status}
                  </span>
                  {activity.user_name && (
                    <span className="text-xs text-gray-500">by {activity.user_name}</span>
                  )}
                  <span className="text-xs text-gray-500">{activity.time}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
        <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          <button 
            onClick={() => navigate('/trial-creation')}
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
          >
            <div className="text-center">
              <Sprout className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900">Create New Trial</p>
              <p className="text-xs text-gray-500">Start a new agricultural trial</p>
            </div>
          </button>
          <button 
            onClick={() => navigate('/location')}
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
          >
            <div className="text-center">
              <MapPin className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900">Add Location</p>
              <p className="text-xs text-gray-500">Register a new location</p>
            </div>
          </button>
          <button 
            onClick={() => navigate('/officer-incharge')}
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
          >
            <div className="text-center">
              <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900">Assign Officer</p>
              <p className="text-xs text-gray-500">Assign officers to trials</p>
            </div>
          </button>
        </div>
      </div>

      {/* Full Screen Map Modal */}
      {showFullMap && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full h-full max-w-7xl max-h-[90vh] relative overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white shadow-sm">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Tamil Nadu Research Trials</h2>
                  <p className="text-sm text-gray-500">Interactive trial location mapping</p>
                </div>
              </div>
              <button
                onClick={() => setShowFullMap(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Full Map Content */}
            <div className="p-6 h-full overflow-auto">
              <div className="bg-white rounded-lg border-2 border-gray-200 h-full flex items-center justify-center relative">
                <svg 
                  viewBox="0 0 800 600" 
                  className="w-full h-full p-8"
                  style={{ maxWidth: '100%', height: 'auto' }}
                >
                  {/* Tamil Nadu State Outline */}
                  <path
                    d="M 100 100 L 300 60 L 500 80 L 640 120 L 700 200 L 680 300 L 640 400 L 560 500 L 400 540 L 240 560 L 160 500 L 120 400 L 100 300 Z"
                    fill="#f0f9ff"
                    stroke="#3b82f6"
                    strokeWidth="3"
                    className="hover:fill-blue-100 transition-colors cursor-pointer"
                  />
                  
                  {/* District Markers */}
                  <circle cx="240" cy="240" r="12" fill="#3b82f6" className="cursor-pointer hover:r-16 transition-all">
                    <title>Coimbatore - 3 Trials</title>
                  </circle>
                  <text x="240" y="280" textAnchor="middle" className="text-base font-medium fill-gray-700">Coimbatore</text>
                  
                  <circle cx="360" cy="200" r="10" fill="#10b981" className="cursor-pointer hover:r-14 transition-all">
                    <title>Salem - 2 Trials</title>
                  </circle>
                  <text x="360" y="240" textAnchor="middle" className="text-base font-medium fill-gray-700">Salem</text>
                  
                  <circle cx="400" cy="280" r="10" fill="#8b5cf6" className="cursor-pointer hover:r-14 transition-all">
                    <title>Erode - 2 Trials</title>
                  </circle>
                  <text x="400" y="320" textAnchor="middle" className="text-base font-medium fill-gray-700">Erode</text>
                  
                  <circle cx="560" cy="360" r="8" fill="#f59e0b" className="cursor-pointer hover:r-12 transition-all">
                    <title>Madurai - 1 Trial</title>
                  </circle>
                  <text x="560" cy="400" textAnchor="middle" className="text-base font-medium fill-gray-700">Madurai</text>
                  
                  <circle cx="640" cy="160" r="10" fill="#ef4444" className="cursor-pointer hover:r-14 transition-all">
                    <title>Chennai - 2 Trials</title>
                  </circle>
                  <text x="640" y="200" textAnchor="middle" className="text-base font-medium fill-gray-700">Chennai</text>
                  
                  <circle cx="480" cy="320" r="8" fill="#06b6d4" className="cursor-pointer hover:r-12 transition-all">
                    <title>Trichy - 1 Trial</title>
                  </circle>
                  <text x="480" y="360" textAnchor="middle" className="text-base font-medium fill-gray-700">Trichy</text>
                </svg>
                
                {/* Map Info Overlay */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-xl border border-gray-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Total Districts</p>
                        <p className="text-lg font-bold text-primary-600">6</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Active Trials</p>
                        <p className="text-lg font-bold text-green-600">15</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">MLT Projects</p>
                        <p className="text-lg font-bold text-blue-600">9</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">ART Projects</p>
                        <p className="text-lg font-bold text-emerald-600">6</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 